import { useEffect, useMemo, useState } from 'react';
import { CategoriesAPI } from '@/api';
import { ICategoryDetails } from '@/models/categories.model';

const PREVIEW_CAP = 12;

/** First N unique ids in array order (for preview row + fetch key). */
export function categoryGalleryPreviewIds(ids: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= PREVIEW_CAP) break;
  }
  return out;
}

/**
 * Loads catalog category details by id (parallel requests) for block canvas preview.
 * Uses direct API calls so Redux categoryDetails is not overwritten.
 */
export function useCategoriesByIdsForPreview(ids: string[]): Map<string, ICategoryDetails> {
  const [byId, setById] = useState<Map<string, ICategoryDetails>>(() => new Map());

  const idKey = useMemo(() => JSON.stringify(categoryGalleryPreviewIds(ids)), [ids]);

  useEffect(() => {
    const unique = JSON.parse(idKey) as string[];
    if (unique.length === 0) {
      setById(new Map());
      return;
    }

    let cancelled = false;

    void Promise.all(
      unique.map((id) =>
        CategoriesAPI.getCategoryDetails(id)
          .then((response) => ({ id, detail: response.data?.data ?? null }))
          .catch(() => ({ id, detail: null as ICategoryDetails | null })),
      ),
    ).then((rows) => {
      if (cancelled) return;
      const next = new Map<string, ICategoryDetails>();
      rows.forEach(({ id, detail }) => {
        if (detail) next.set(id, detail);
      });
      setById(next);
    });

    return () => {
      cancelled = true;
    };
  }, [idKey]);

  return byId;
}
